// Activity tracking service for logging user activities
import { getAdminApiBaseUrl } from '@/lib/adminApi';

export interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  userType: 'employee' | 'music-vendor' | 'installer' | 'client';
  userEmail: string;
  activity: string;
  activityType: 'login' | 'logout' | 'page-view' | 'action' | 'data-change' | 'system';
  pageUrl?: string;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  sessionDuration?: number;
  details?: string;
  status: 'success' | 'error' | 'warning';
}

const API_BASE_URL = getAdminApiBaseUrl();

class ActivityTracker {
  private static instance: ActivityTracker;
  private getToken: (() => Promise<string>) | null = null;

  private constructor() {}
  
  // Set the token getter function (called from components that have Auth0 access)
  public setTokenGetter(getToken: () => Promise<string>): void {
    this.getToken = getToken;
  }

  public static getInstance(): ActivityTracker {
    if (!ActivityTracker.instance) {
      ActivityTracker.instance = new ActivityTracker();
    }
    return ActivityTracker.instance;
  }


  // Log Super Admin page view to native governance activity history.
  public async logSaPageView(input: {
    routePath: string;
    pageTitle: string;
    moduleName: string;
    userName?: string;
    userEmail?: string;
  }): Promise<void> {
    if (!this.getToken) {
      return;
    }

    try {
      const token = await this.getToken();
      const response = await fetch(`${API_BASE_URL}/sa/activity-history/page-views`, {
        method: "POST",
        credentials: "omit",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          routePath: input.routePath,
          pageTitle: input.pageTitle,
          moduleName: input.moduleName,
        }),
      });

      if (!response.ok) {
        console.warn("Failed to record SA page view:", response.status);
      }
    } catch (error) {
      console.warn("Error recording SA page view:", error);
    }
  }

  // Log a user activity (async - sends to legacy proxy API)
  public async logActivity(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    activity: string,
    activityType: 'login' | 'logout' | 'page-view' | 'action' | 'data-change' | 'system',
    details?: string,
    pageUrl?: string,
    status: 'success' | 'error' | 'warning' = 'success'
  ): Promise<void> {
    if (!this.getToken) {
      console.warn('ActivityTracker: Token getter not set. Activity will not be logged.');
      return;
    }

    try {
      const token = await this.getToken();
      
      const activityData = {
        activity,
        activityType,
        pageUrl: pageUrl || window.location.pathname,
        details,
        status,
        userType,
        userName,
        sessionDuration: undefined, // Can be set by specific log methods
      };

      const response = await fetch(`${API_BASE_URL}/activity`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(activityData),
      });

      if (!response.ok) {
        console.error('Failed to log activity:', response.statusText);
      } else {
        console.log('Activity logged successfully');
      }
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging should not break the app
    }
  }

  // Log user login
  public async logLogin(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    sessionDuration?: number
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      'User logged in successfully',
      'login',
      undefined,
      '/login',
      'success'
    );
  }

  // Log user logout
  public async logLogout(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      'User logged out',
      'logout',
      undefined,
      window.location.pathname,
      'success'
    );
  }

  // Log page view
  public async logPageView(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    pageName: string,
    details?: string
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      `Viewed ${pageName} page`,
      'page-view',
      details,
      window.location.pathname,
      'success'
    );
  }

  // Log user action
  public async logAction(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    action: string,
    details?: string
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      action,
      'action',
      details,
      window.location.pathname,
      'success'
    );
  }

  // Log data change
  public async logDataChange(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    change: string,
    details?: string
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      change,
      'data-change',
      details,
      window.location.pathname,
      'success'
    );
  }

  // Log error
  public async logError(
    userId: string,
    userName: string,
    userType: 'employee' | 'music-vendor' | 'installer' | 'client',
    userEmail: string,
    error: string,
    details?: string
  ): Promise<void> {
    await this.logActivity(
      userId,
      userName,
      userType,
      userEmail,
      error,
      'system',
      details,
      window.location.pathname,
      'error'
    );
  }

  // Get all activities from API
  public async getActivities(options?: {
    userId?: string;
    activityType?: string;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ActivityLog[]> {
    if (!this.getToken) {
      console.warn('ActivityTracker: Token getter not set. Cannot fetch activities.');
      return [];
    }

    try {
      const token = await this.getToken();
      const params = new URLSearchParams();
      
      if (options?.userId) params.append('userId', options.userId);
      if (options?.activityType) params.append('activityType', options.activityType);
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);

      const url = `${API_BASE_URL}/activity${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch activities:', response.statusText);
        return [];
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching activities:', error);
      return [];
    }
  }

  // Get activities by user type
  public async getActivitiesByUserType(userType: string): Promise<ActivityLog[]> {
    const activities = await this.getActivities();
    return activities.filter(activity => activity.userType === userType);
  }

  // Get activities by user ID
  public async getActivitiesByUserId(userId: string): Promise<ActivityLog[]> {
    return await this.getActivities({ userId });
  }

  // Get activities by date range
  public async getActivitiesByDateRange(startDate: Date, endDate: Date): Promise<ActivityLog[]> {
    return await this.getActivities({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
  }

  // Export activities as JSON (fetches from API)
  public async exportActivities(): Promise<string> {
    const activities = await this.getActivities();
    return JSON.stringify(activities, null, 2);
  }
}

// Create and export a singleton instance
export const activityTracker = ActivityTracker.getInstance();

// Export the class for testing purposes
export default ActivityTracker;
